import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Pizza, Mail, MessageSquare, Calendar, XOctagon, RotateCcw, CreditCard, AlertTriangle, Gift, Ticket, QrCode, Lock, CheckCircle2, Sparkles, Utensils, Newspaper, ArrowRight, Check } from "lucide-react";
import { BTN_PRIMARY, BTN_SM, CARD } from "@/lib/theme";

const BTN_GHOST = "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#17324D] border border-[#17324D]/20 hover:border-[#17324D] transition-colors";

const statusColor = {
  active: "bg-[#17324D] text-white",
  past_due: "bg-[#D94A3D]/10 text-[#D94A3D]",
  canceled: "bg-[#D94A3D] text-white",
  expired: "bg-[#17324D]/60 text-white",
};

export default function CustomerDashboard() {
  const [memberships, setMemberships] = useState([]);
  const [comms, setComms] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voucherConfirm, setVoucherConfirm] = useState(null);
  const [redeemingVoucher, setRedeemingVoucher] = useState(null);

  const load = () => {
    Promise.all([
      api.get("/memberships/mine"), api.get("/communications/mine"),
      api.get("/rewards"), api.get("/redemptions/mine"),
    ]).then(([m, c, rw, rd]) => {
      setMemberships(m.data); setComms(c.data); setRewards(rw.data); setRedemptions(rd.data);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const redeem = async (id) => {
    try {
      const { data } = await api.post(`/rewards/${id}/redeem`);
      toast.success(`Redeemed! Show code ${data.code} at the counter.`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cancel = async (id) => {
    try {
      const { data } = await api.post(`/memberships/${id}/cancel`);
      toast.success(`Set to cancel. You stay Active until ${data.active_until ? new Date(data.active_until).toLocaleDateString() : "period end"}.`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const resume = async (id) => {
    try {
      await api.post(`/memberships/${id}/resume`);
      toast.success("Membership resumed — you're all set!");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const reactivate = async (rid) => {
    try {
      const { data } = await api.post("/checkout/subscribe", { restaurant_id: rid, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const redeemVoucher = async (mid) => {
    setRedeemingVoucher(mid);
    try {
      const { data } = await api.post(`/vouchers/${mid}/redeem`);
      setVoucherConfirm(data);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRedeemingVoucher(null); }
  };

  const activeCount = memberships.filter((m) => m.status === "active").length;
  const readyVouchers = memberships.filter((m) => m.status === "active" && m.voucher?.available).length;
  const SECTIONS = [
    { id: "memberships", label: "Memberships", icon: CreditCard },
    { id: "vouchers", label: "Vouchers", icon: Ticket },
    { id: "rewards", label: "Rewards", icon: Gift },
    { id: "redemptions", label: "My Codes", icon: QrCode },
    { id: "inbox", label: "Inbox", icon: Mail },
  ];
  const Section = ({ id, title, sub, children }) => (
    <section id={id} data-testid={`section-${id}`} className="mt-14 scroll-mt-28">
      <h2 className="font-heading font-bold text-2xl">{title}</h2>
      {sub && <p className="text-sm text-[#17324D]/60 mt-1 mb-5">{sub}</p>}
      {children}
    </section>
  );
  const Pill = ({ cls, children }) => <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase ${cls}`}>{children}</span>;
  const Note = ({ testid, children }) => (
    <div data-testid={testid} className="mt-4 p-3 rounded-xl bg-[#D94A3D]/10 flex gap-2 items-start">
      <AlertTriangle size={16} className="text-[#D94A3D] mt-0.5 shrink-0" />
      <p className="text-sm">{children}</p>
    </div>
  );
  const activeVoucherMemberships = memberships.filter((m) => m.status === "active" && m.voucher);

  return (
    <div className="App min-h-screen bg-[#FFF4E6] text-[#17324D]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 py-10 md:py-12">
        <div className="flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="text-xs sm:text-sm font-bold tracking-[0.18em] uppercase text-[#D94A3D]">Member dashboard</p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mt-1">My Memberships</h1>
            <p className="text-sm text-[#17324D]/60 mt-1">{activeCount} active membership{activeCount === 1 ? "" : "s"}{readyVouchers ? ` · ${readyVouchers} voucher${readyVouchers === 1 ? "" : "s"} ready` : ""}</p>
          </div>
          <Link to="/card" data-testid="open-card-btn" className={BTN_SM}>
            <QrCode size={18} /> My Membership Card
          </Link>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-3 gap-3 mt-8" data-testid="quick-links">
          {[
            { to: "/card", icon: QrCode, t: "My Card", d: "Show at the counter", id: "ql-card" },
            { to: "/clubs", icon: Utensils, t: "Browse Clubs", d: "Join another club", id: "ql-clubs" },
            { to: "/blog", icon: Newspaper, t: "Blog", d: "News from your clubs", id: "ql-blog" },
          ].map((q) => (
            <Link key={q.id} to={q.to} data-testid={q.id} className={`${CARD} p-4 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(23,50,77,0.12)] transition-[transform,box-shadow]`}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#D94A3D]/10 text-[#D94A3D]"><q.icon size={18} /></span>
              <p className="font-heading font-bold text-sm sm:text-base mt-3">{q.t}</p>
              <p className="text-xs text-[#17324D]/60 hidden sm:block">{q.d}</p>
            </Link>
          ))}
        </div>

        {/* Section nav */}
        <nav data-testid="member-section-nav" className="mt-8 flex gap-1 overflow-x-auto border-b border-[#17324D]/10 -mx-5 px-5 sm:mx-0 sm:px-0 sticky top-16 bg-[#FFF4E6]/95 backdrop-blur z-30">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} data-testid={`member-nav-${s.id}`}
              className="inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap text-[#17324D]/60 hover:text-[#D94A3D] border-b-2 border-transparent hover:border-[#D94A3D] -mb-px transition-colors">
              <s.icon size={15} /> {s.label}
            </a>
          ))}
        </nav>

        {/* ---------- Memberships ---------- */}
        <Section id="memberships" title="Memberships" sub="Your clubs, renewal dates and perks.">
          {loading ? <p className="text-[#17324D]/60">Loading...</p> : memberships.length === 0 ? (
            <div className={`${CARD} p-10 text-center`}>
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#D94A3D]/10 text-[#D94A3D]"><Pizza size={28} /></span>
              <p className="mt-4 font-heading font-bold text-lg">No memberships yet.</p>
              <Link to="/clubs" className={`${BTN_SM} mt-4`}>Browse clubs to join <ArrowRight size={15} /></Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {memberships.map((m) => (
                <div key={m.id} data-testid={`membership-card-${m.id}`} className={`${CARD} p-6 flex flex-col`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <h3 className="font-heading font-bold text-xl leading-tight">{m.restaurant?.name || "Club"}</h3>
                      <p className="text-sm text-[#17324D]/60 mt-1">${m.restaurant?.monthly_price}/month · {m.restaurant?.winback_discount}% off every order</p>
                    </div>
                    <Pill cls={statusColor[m.status] || "bg-[#17324D]/60 text-white"}>{m.status.replace("_", " ")}</Pill>
                  </div>
                  <p className="mt-4 text-sm flex items-center gap-2 text-[#17324D]/80">
                    <Calendar size={14} className="text-[#D94A3D]" />
                    {m.status === "past_due" ? "Period ended" : m.status === "canceled" ? "Ended" : "Renews"}: <span className="font-semibold">{m.current_period_end ? new Date(m.current_period_end).toLocaleDateString() : "—"}</span>
                  </p>
                  {(m.restaurant?.perks || []).length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {m.restaurant.perks.slice(0, 3).map((p, i) => <li key={i} className="text-sm flex gap-2"><Check size={15} strokeWidth={3} className="text-[#D94A3D] mt-0.5 shrink-0" />{p}</li>)}
                    </ul>
                  )}
                  {m.status === "past_due" && (
                    <Note testid={`pastdue-banner-${m.id}`}>Your last payment failed. Update your card and renew to keep your perks (reminder {m.dunning_attempts || 1}).</Note>
                  )}
                  {m.status === "active" && m.cancel_at_period_end && (
                    <Note testid={`scheduled-cancel-${m.id}`}>Cancels on {m.current_period_end ? new Date(m.current_period_end).toLocaleDateString() : "period end"} — you stay Active until then.</Note>
                  )}
                  <div className="mt-5 pt-4 border-t border-[#17324D]/10 flex gap-2 flex-wrap">
                    {m.status === "active" ? (
                      m.cancel_at_period_end ? (
                        <button data-testid={`resume-btn-${m.id}`} onClick={() => resume(m.id)} className={BTN_SM}><RotateCcw size={15} /> Resume</button>
                      ) : (
                        <button data-testid={`cancel-btn-${m.id}`} onClick={() => cancel(m.id)} className={BTN_GHOST}><XOctagon size={15} /> Cancel</button>
                      )
                    ) : m.status === "past_due" ? (
                      <button data-testid={`renew-btn-${m.id}`} onClick={() => reactivate(m.restaurant_id)} className={BTN_SM}><CreditCard size={15} /> Update Card & Renew</button>
                    ) : (
                      <button data-testid={`reactivate-btn-${m.id}`} onClick={() => reactivate(m.restaurant_id)} className={BTN_SM}><RotateCcw size={15} /> Reactivate</button>
                    )}
                    <Link to={`/clubs/${m.restaurant_id}`} data-testid={`view-club-${m.id}`} className={BTN_GHOST}>Club info <ArrowRight size={14} /></Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ---------- Vouchers ---------- */}
        <Section id="vouchers" title="Monthly vouchers" sub="One free item per calendar month at each club that offers it. Resets on the 1st.">
          {activeVoucherMemberships.length === 0 ? (
            <p className="text-[#17324D]/60">No vouchers yet — join a club that offers a monthly free item.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {activeVoucherMemberships.map((m) => (
                <div key={m.id} data-testid={`voucher-${m.id}`}
                  className={`rounded-2xl p-6 ${m.voucher.available ? "bg-[#17324D] text-white" : `${CARD}`}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${m.voucher.available ? "bg-white/10" : "bg-[#D94A3D]/10 text-[#D94A3D]"}`}><Ticket size={20} /></span>
                      <div>
                        <p className="font-heading font-bold text-lg leading-tight">Free {m.voucher.item}</p>
                        <p className={`text-xs ${m.voucher.available ? "text-white/70" : "text-[#17324D]/60"}`}>{m.restaurant?.name}</p>
                      </div>
                    </div>
                    <Pill cls={m.voucher.available ? "bg-[#D94A3D] text-white" : "bg-[#17324D]/10 text-[#17324D]/70"}>{m.voucher.available ? "Ready" : "Used"}</Pill>
                  </div>
                  {m.voucher.available ? (
                    <>
                      <p className="text-sm text-white/80 mt-4">Your monthly free {m.voucher.item.toLowerCase()} is ready. Tap redeem at the counter.</p>
                      <button data-testid={`voucher-redeem-${m.id}`} disabled={redeemingVoucher === m.id} onClick={() => redeemVoucher(m.id)}
                        className={`${BTN_SM} mt-4 w-full py-3 text-base`}>
                        {redeemingVoucher === m.id ? "Redeeming…" : "Redeem Voucher"}
                      </button>
                    </>
                  ) : (
                    <div data-testid={`voucher-cooldown-${m.id}`} className="mt-4 py-2.5 px-3 rounded-xl bg-[#FFF4E6] text-[#17324D]/70 text-sm text-center font-semibold flex items-center justify-center gap-2">
                      <CheckCircle2 size={16} /> Redeemed — next voucher available {m.voucher.next_label}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ---------- Rewards ---------- */}
        <Section id="rewards" title="Available rewards" sub="Rewards unlock at restaurants where you're an active member.">
          {rewards.length === 0 ? (
            <p className="text-[#17324D]/60">No rewards available yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.map((r) => {
                const joined = memberships.some((m) => m.restaurant_id === r.restaurant_id && m.status === "active");
                return (
                  <div key={r.id} data-testid={`avail-reward-${r.id}`} className={`${CARD} p-5 flex flex-col`}>
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="font-heading font-bold text-lg leading-tight">{r.title}</h3>
                      <Pill cls="bg-[#D94A3D]/10 text-[#D94A3D] whitespace-nowrap">{r.reward_type.replace("_", " ")}</Pill>
                    </div>
                    <p className="text-xs text-[#17324D]/60 uppercase tracking-wide mt-1">{r.restaurant_name}</p>
                    <p className="text-sm text-[#17324D]/80 mt-2 flex-1">{r.description}</p>
                    {joined ? (
                      <button data-testid={`redeem-btn-${r.id}`} onClick={() => redeem(r.id)} className={`${BTN_SM} mt-4`}>Redeem</button>
                    ) : (
                      <Link data-testid={`join-to-redeem-${r.id}`} to={`/clubs/${r.restaurant_id}`}
                        className="mt-4 px-4 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 bg-[#FFF4E6] text-[#17324D]/70 rounded-xl hover:bg-[#17324D]/10 transition-colors">
                        <Lock size={13} /> Members only — Join to unlock
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* ---------- Redemptions ---------- */}
        <Section id="redemptions" title="My redemption codes" sub="Show these codes at the restaurant.">
          {redemptions.length === 0 ? (
            <p className="text-[#17324D]/60">You haven't redeemed anything yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {redemptions.map((rd) => (
                <div key={rd.id} data-testid={`my-redemption-${rd.id}`} className={`${CARD} p-5 flex justify-between items-center gap-3`}>
                  <div>
                    <p className="font-semibold">{rd.reward_title}</p>
                    <p className="text-xs text-[#17324D]/60 uppercase tracking-wide">{rd.restaurant_name}</p>
                    <p className="font-mono text-lg mt-1 tracking-wider">{rd.code}</p>
                  </div>
                  <Pill cls={rd.status === "fulfilled" ? "bg-[#17324D] text-white" : "bg-[#D94A3D]/10 text-[#D94A3D]"}>{rd.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ---------- Inbox ---------- */}
        <Section id="inbox" title="Your inbox" sub="Email & text updates from your clubs.">
          {comms.length === 0 ? (
            <p className="text-[#17324D]/60">No messages yet.</p>
          ) : (
            <div className={`${CARD} divide-y divide-[#17324D]/10`}>
              {comms.map((c) => (
                <div key={c.id} data-testid={`comm-${c.id}`} className="p-5 flex gap-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#D94A3D]/10 text-[#D94A3D] shrink-0">
                    {c.channel === "sms" ? <MessageSquare size={16} /> : <Mail size={16} />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold">{c.subject}</p>
                    <p className="text-sm text-[#17324D]/80 mt-0.5">{c.body}</p>
                    <p className="text-xs text-[#17324D]/50 mt-1.5 uppercase">{c.channel} · {new Date(c.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Full-screen voucher redemption confirmation */}
      {voucherConfirm && (
        <div data-testid="voucher-confirm-overlay"
          className="fixed inset-0 z-[100] bg-[#17324D] text-white flex flex-col items-center justify-center px-6 text-center">
          <CheckCircle2 size={140} strokeWidth={2.2} className="text-[#FFF4E6]" />
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-6">Voucher Redeemed!</h1>
          <p className="mt-3 text-2xl md:text-3xl flex items-center gap-2 text-[#FFF4E6] font-bold">
            <Sparkles size={26} className="text-[#D94A3D]" /> Free {voucherConfirm.item}
          </p>
          <div className="mt-8 bg-white/10 rounded-2xl px-8 py-5 backdrop-blur">
            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Member</p>
            <p data-testid="voucher-confirm-name" className="font-heading font-bold text-2xl mt-1">{voucherConfirm.member_name}</p>
            <p className="mt-3 text-sm uppercase tracking-[0.2em] text-white/70">Redeemed</p>
            <p className="text-lg font-semibold">{new Date(voucherConfirm.redeemed_at).toLocaleString()}</p>
            <p className="text-sm text-white/70 mt-1">{voucherConfirm.restaurant_name}</p>
          </div>
          <button data-testid="voucher-confirm-done" onClick={() => setVoucherConfirm(null)} className={`${BTN_PRIMARY} mt-10 px-10 shadow-none hover:shadow-none hover:translate-x-0 hover:translate-y-0`}>
            Done
          </button>
          <p className="mt-4 text-sm text-white/70">Show this screen to the staff at the counter.</p>
        </div>
      )}
    </div>
  );
}
